/**
 * resendCooldown — the shared throttle behind every "resend one-time code" control.
 *
 * Stores an absolute expiry timestamp rather than counting ticks, so a backgrounded tab or a
 * sleeping phone can't under-count its way to an early resend. The deadline lives in
 * sessionStorage (via storageMode, which falls back to memory when storage is unavailable and
 * is safe during SSR) so a page refresh or a modal reopen resumes the remaining time instead of
 * granting a fresh window.
 */
import { useState, useEffect, useCallback } from 'react'
import { storageMode } from './storage'

export const RESEND_COOLDOWN_SECONDS = 60

/**
 * resendCooldownKey: build the storage key for one destination's cooldown.
 *
 * @param {string} namespace The surface the code was sent from — 'phone' | 'email' | 'smsVerify'.
 *   Namespaces are kept distinct so a lead-form verification can't lock out an auth resend for
 *   the same phone number.
 * @param {string} destination The phone number or email address the code was sent to
 * @return {string|null} The key, or null when there is no destination to key on
 */
export const resendCooldownKey = (namespace, destination) =>
  destination ? `resendCooldown:${namespace}:${destination}` : null

/**
 * getResendCooldownRemaining: whole seconds left on a cooldown, derived from the stored deadline.
 *
 * @param {string|null} key A key from resendCooldownKey
 * @return {number} Seconds remaining; 0 when there is no cooldown or it has already expired
 */
export const getResendCooldownRemaining = (key) => {
  if (!key) {
    return 0
  }

  const deadline = Number(storageMode('sessionStorage').getItem(key))

  if (!deadline) {
    return 0
  }

  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
}

/**
 * startResendCooldown: open a fresh cooldown window for a destination.
 *
 * @param {string|null} key A key from resendCooldownKey
 * @param {number} [seconds] Length of the window
 * @return {void}
 */
export const startResendCooldown = (key, seconds = RESEND_COOLDOWN_SECONDS) => {
  if (!key) {
    return
  }

  storageMode('sessionStorage').setItem(key, String(Date.now() + seconds * 1000))
}

/**
 * clearResendCooldown: drop a cooldown window, whether or not it has expired.
 *
 * @param {string|null} key A key from resendCooldownKey
 * @return {void}
 */
export const clearResendCooldown = (key) => {
  if (!key) {
    return
  }

  storageMode('sessionStorage').removeItem(key)
}

/**
 * useResendCooldown: live countdown for a resend control.
 *
 * @param {string|null} key A key from resendCooldownKey. Passing null (no destination yet)
 *   yields a permanently idle cooldown.
 * @param {number} [seconds] Length of the window opened by startCooldown
 * @return {{secondsRemaining: number, isCoolingDown: boolean, startCooldown: function(): void}}
 */
export const useResendCooldown = (key, seconds = RESEND_COOLDOWN_SECONDS) => {
  const [secondsRemaining, setSecondsRemaining] = useState(() => getResendCooldownRemaining(key))
  const [cycle, setCycle] = useState(0)

  const startCooldown = useCallback(() => {
    startResendCooldown(key, seconds)
    setCycle((current) => current + 1)
  }, [key, seconds])

  useEffect(() => {
    const tick = () => {
      const remaining = getResendCooldownRemaining(key)
      setSecondsRemaining(remaining)

      if (remaining <= 0) {
        clearResendCooldown(key)
        clearInterval(interval)
      }
    }

    const interval = setInterval(tick, 1000)
    tick()

    return () => clearInterval(interval)
  }, [key, cycle])

  return { secondsRemaining, isCoolingDown: secondsRemaining > 0, startCooldown }
}
